import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { Helmet } from 'react-helmet-async';

const Support = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const faqs = [
    {
      question: t('support.faq1Question'),
      answer: t('support.faq1Answer')
    },
    {
      question: t('support.faq2Question'),
      answer: t('support.faq2Answer')
    },
    {
      question: t('support.faq3Question'),
      answer: t('support.faq3Answer')
    },
    {
      question: t('support.faq4Question'),
      answer: t('support.faq4Answer')
    },
    {
      question: t('support.faq5Question'),
      answer: t('support.faq5Answer')
    },
    {
      question: t('support.faq6Question'),
      answer: t('support.faq6Answer')
    },
    {
      question: t('support.faq7Question'),
      answer: t('support.faq7Answer')
    },
    {
      question: t('support.faq8Question'),
      answer: t('support.faq8Answer')
    },
    // FAQ supplémentaires pour améliorer le SEO
    {
      question: "BookingBarber est-il vraiment le service le moins cher au monde ?",
      answer: "Oui ! BookingBarber propose les tarifs les plus compétitifs du marché mondial pour les professionnels de la barberie. À partir de 25€/mois seulement, notre mission est d'aider tous les salons à prospérer, pas de maximiser nos profits."
    },
    {
      question: "Quels types de salons peuvent utiliser BookingBarber ?",
      answer: "BookingBarber est parfait pour tous les professionnels de la barberie : barbiers, coiffeurs hommes, barbiers traditionnels, salons de barbier, et bien plus encore."
    },
    {
      question: "Comment BookingBarber améliore-t-il mon référencement Google ?",
      answer: "BookingBarber optimise automatiquement votre présence en ligne : fiche Google Business, collecte d'avis clients, site web optimisé SEO, et présence sur notre plateforme qui améliore votre visibilité locale."
    },
    {
      question: "Puis-je utiliser BookingBarber pour plusieurs salons ?",
      answer: "Oui, vous pouvez gérer plusieurs établissements avec un seul compte. Chaque salon aura sa propre fiche, son planning et ses paramètres personnalisés."
    }
  ];

  // Generate structured data for FAQ
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Support BookingBarber - Centre d'Aide pour Professionnels de la Barberie</title>
        <meta name="description" content="Centre d'aide BookingBarber : FAQ complète, support technique et guides pour les professionnels de la barberie. Service client réactif et solutions à tous vos problèmes." />
        <meta name="keywords" content="support bookingbarber, aide salon barberie, FAQ réservation en ligne, centre d'aide, support technique, service client bookingbarber" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bookingbarber.fr/support" />
        <meta property="og:title" content="Support BookingBarber - Centre d'Aide pour Professionnels de la Barberie" />
        <meta property="og:description" content="Centre d'aide complet avec FAQ, support technique et guides pour optimiser votre salon de barbier." />
        <meta property="og:image" content="https://bookingbarber.com/Logos/logoFocus.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Support BookingBarber - Centre d'Aide" />
        <meta name="twitter:description" content="FAQ complète et support technique pour les professionnels de la beauté." />
        
        <link rel="canonical" href="https://bookingbarber.com/support" />
        
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>
    <Box>
      <AppBar position="static" sx={{ backgroundColor: 'white', color: 'black', boxShadow: 1 }}>
        <Toolbar>
          <IconButton onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            {t('support.helpCenter')}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box textAlign="center" mb={6}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('support.helpCenter')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            {t('support.findAnswers')}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 4, border: '1px solid #e0e0e0' }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                {t('support.faq')}
              </Typography>
              
              {faqs.map((faq, index) => (
                <Accordion key={index} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1">
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ '& > *': { mb: 3 } }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <HelpIcon sx={{ fontSize: 40, color: '#212121', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {t('support.needHelp')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('support.supportTeamHere')}
                  </Typography>
                  <Button
                    variant="contained"
                    href="mailto:support@omnivers.fr"
                    sx={{
                      backgroundColor: '#212121',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#424242'
                      }
                    }}
                  >
                    {t('support.contactSupport')}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <EmailIcon sx={{ fontSize: 40, color: '#212121', mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {t('support.emailSupport')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    support@omnivers.fr
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('support.response24h')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('support.businessHours')}
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {t('support.usefulGuides')}
                  </Typography>
                  <Box sx={{ textAlign: 'left' }}>
                    <Button
                      variant="text"
                      onClick={() => navigate('/features')}
                      sx={{ display: 'block', mb: 1, textAlign: 'left' }}
                    >
                      → {t('support.discoverFeatures')}
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => navigate('/pricing')}
                      sx={{ display: 'block', mb: 1, textAlign: 'left' }}
                    >
                      → {t('support.viewPricing')}
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => navigate('/auth?mode=signup')}
                      sx={{ display: 'block', textAlign: 'left' }}
                    >
                      → {t('support.createAccount')}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>

        <Box mt={6}>
          <Paper elevation={0} sx={{ p: 4, border: '1px solid #e0e0e0', backgroundColor: '#f5f5dc', textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('support.cantFindAnswer')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('support.supportTeamAvailable')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="mailto:support@omnivers.fr"
              sx={{
                backgroundColor: '#212121',
                color: 'white',
                fontWeight: 'bold',
                px: 4,
                py: 2,
                '&:hover': {
                  backgroundColor: '#424242'
                }
              }}
            >
              {t('support.contactUs')}
            </Button>
          </Paper>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default Support;